import Coordinate from "../Coordinates/Coordinate";
import Wms, {WmsParams} from "./Wms";
import Cutout from "../Main/Cutout";
import Cache from "../Util/Cache";
import Container from "../Main/Container";
import Projection, {ProjectionReactiveProps} from "./Projection";
import {GridSpec} from "../Main/Grid";
import {Serialization} from "../Main/Serializer";
import {copyObject, throttledParallelEachPromise} from "../Util/functions";
import {median} from "../Util/Math.js";

const MM_PER_INCH = 25.4;

type _WmsProjectionReactiveProps = {
    type: 'WmsProjection',
    dpi: number|null,
    eraseDrawnGrid: boolean|null,
}
export type WmsProjectionReactiveProps = ProjectionReactiveProps & _WmsProjectionReactiveProps;

export default class WmsProjection<C extends Coordinate> extends Projection<C, Wms> {

    reactiveProps: WmsProjectionReactiveProps;

    private dpi: number|null;
    private eraseDrawnGrid: boolean|null;

    static createAndInitialize(wmsName: string, scale: number = null): Promise<WmsProjection<Coordinate>> {
        return new Promise<WmsProjection<Coordinate>>((resolve, reject) => {
            const projection = new WmsProjection(wmsName, scale);
            return projection.initialize().then(() => {
                resolve(projection);
            });
        });
    }

    constructor(wmsName: string, scale: number = null) {
        const wms = Container.wms(wmsName);

        super(wms, scale, <_WmsProjectionReactiveProps>{
            type: 'WmsProjection',
            dpi: null,
            eraseDrawnGrid: null,
        });

        this.dpi = null;
        this.eraseDrawnGrid = null;
    }

    initialize(): Promise<void> {
        return this.mapImageProvider.fetchCapabilities().then(() => {
            if(this.scale === null) {
                this.reactiveProps.scale = this.scale = this.mapImageProvider.getDefaultScale();
            }
        });
    }

    clone(): WmsProjection<C> {
        return new WmsProjection(
            this.mapImageProvider.name,
            this.getScale(),
        );
    }

    serialize(): Serialization {
        return {
            type: 'wms',
            mip: this.mapImageProvider.name,
            scale: this.scale,
            dpi: this.dpi,
            eraseDrawnGrid: this.eraseDrawnGrid,
        };
    }

    static unserialize(serialized: Serialization): WmsProjection<Coordinate> {
        const projection = new WmsProjection(serialized.mip, serialized.scale);
        if(serialized.dpi) {
            projection.setDpi(serialized.dpi);
        }
        if (serialized.eraseDrawnGrid !== null && serialized.eraseDrawnGrid !== undefined) {
            projection.setEraseDrawnGrid(serialized.eraseDrawnGrid);
        }
        return projection;
    }

    getCopyright() {
        let copyright = super.getCopyright();

        if (this.mapImageProvider.hasDrawnGrid() && this.getEraseDrawnGrid()) {
            copyright += ' (bewerkt)';
        }

        return copyright;
    }

    resetAdvancedSettings(): Partial<this> {
        const advancedSettings = <Partial<this>>super.resetAdvancedSettings();

        advancedSettings.dpi = this.dpi;
        this.setDpi(null);

        advancedSettings.eraseDrawnGrid = this.eraseDrawnGrid;
        this.setEraseDrawnGrid(null);

        return advancedSettings;
    }

    reapplyAdvancedSettings(advancedSettings: Partial<this>): void {
        super.reapplyAdvancedSettings(<Partial<Projection<C, Wms>>>advancedSettings);

        this.setDpi(advancedSettings.dpi);
        this.setEraseDrawnGrid(advancedSettings.eraseDrawnGrid);
    }

    attach(cutout: Cutout<any, C, any>) {
        super.attach(cutout);

        // Preload capabilities upon attaching
        this.initialize();
    }

    getMipDrawnGrid(): GridSpec | null {
        return this.mapImageProvider.getDrawnGrid();
    }

    hasAutoDpi(): boolean {
        return this.dpi === null;
    }

    getRawDpi(): number|null {
        return this.dpi;
    }

    getDpi(): number {
        return this.dpi || this.computeDynamicDpi();
    }

    setDpi(newDpi: number) {
        this.reactiveProps.dpi = this.dpi = newDpi;
    }

    private computeDynamicDpi(): number {
        const defaultDpi = this.mapImageProvider.getDefaultDpi() || 300;

        return Math.ceil(defaultDpi / this.mapImageProvider.getDefaultScale() * this.scale);
    }

    getEraseDrawnGrid(): boolean|null {
        return this.eraseDrawnGrid;
    }

    setEraseDrawnGrid(newEraseDrawnGrid: boolean) {
        this.reactiveProps.eraseDrawnGrid = this.eraseDrawnGrid = newEraseDrawnGrid;
    }

    getWmsUrl(coords: C[], params: WmsParams = {}) {
        return this.mapImageProvider.mapUrl(copyObject(params, {
            bbox: coords[3].getX() + ',' + coords[3].getY() + ',' + coords[1].getX() + ',' + coords[1].getY(),
        }));
    }

    isWithinSuggestedScaleRange(): Promise<any> {
        // WMS 1.3.0, Section 7.2.4.6.9 Scale denominators:
        //   "(...), the common pixel size is defined to be 0,28 mm × 0,28 mm."
        // (http://portal.opengeospatial.org/files/?artifact_id=14416)

        return this.mapImageProvider.getSuggestedScaleRange().then((suggestedScaleRange) => {
            const ptPerMm = this.getDpi() / 25.4;

            const effectiveScale = this.getScale() / ptPerMm / 0.28;

            return {
                isWithin: () => {
                    if(suggestedScaleRange.min !== null && effectiveScale < suggestedScaleRange.min) {
                        return false;
                    }

                    if(suggestedScaleRange.max !== null && effectiveScale > suggestedScaleRange.max) {
                        return false;
                    }

                    return true;
                },
                minMaxScale: () => {
                    let ret = {min: null, max: null};

                    if(suggestedScaleRange.min !== null) {
                        ret.min = suggestedScaleRange.min * ptPerMm * 0.28;
                    }
                    if(suggestedScaleRange.max !== null) {
                        ret.max = suggestedScaleRange.max * ptPerMm * 0.28;
                    }

                    return ret;
                },
                minMaxDpi: () => {
                    let ret = {min: null, max: null};

                    if(suggestedScaleRange.min !== null) {
                        ret.max = this.getScale() / suggestedScaleRange.min / 0.28 * 25.4;
                    }
                    if(suggestedScaleRange.max !== null) {
                        ret.min = this.getScale() / suggestedScaleRange.max / 0.28 * 25.4;
                    }

                    return ret;
                },
            };
        });
    }

    private getTileDimensions() {
        // real mm: mm in physical world
        // paper mm: mm on paper map
        // tile: one WMS image download
        // px: pixels in WMS tile download
        // unit: unit of measurement of projection coordinate system (e.g., meters)
        const scale = this.getScale();
        const dpi = this.getDpi();
        const realMmPerPaperMm = scale;

        const realMmPerUnit = 1000;
        const targetPxPerTile = 500;
        const paperMmPerUnit = realMmPerUnit / realMmPerPaperMm;

        const pxPerPaperMm = dpi / MM_PER_INCH;
        const pxPerUnit = pxPerPaperMm * paperMmPerUnit;

        const targetUnitsPerTile = targetPxPerTile / pxPerUnit;
        const targetUnitsPerTileOrder = 10 ** Math.floor(Math.log10(targetUnitsPerTile));
        const unitsPerTile = Math.round(targetUnitsPerTile / targetUnitsPerTileOrder) * targetUnitsPerTileOrder;

        const pxPerTile = Math.round(pxPerUnit * unitsPerTile);

        // Compute back real values
        const out_unitsPerTile = unitsPerTile;
        const out_pxPerTile = pxPerTile;
        const out_pxPerUnit = out_pxPerTile / out_unitsPerTile;
        const out_paperMmPerTile = paperMmPerUnit * out_unitsPerTile;

        return {
            pxPerUnit: out_pxPerUnit,
            unitsPerTile: out_unitsPerTile,
            pxPerTile: out_pxPerTile,
            paperMmPerTile: out_paperMmPerTile,
        };
    }

    protected fetchForProject(
        cache: Cache,
        progressCallback: ((evt) => void)|null,
        minX: number,
        maxX: number,
        minY: number,
        maxY: number,
        callback: (img: HTMLImageElement, coordinate: C, metrics: {pxPerUnit: number, paperMmPerTile: number}) => void
    ): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const {
                pxPerUnit,
                unitsPerTile,
                pxPerTile,
                paperMmPerTile,
            } = this.getTileDimensions();

            minX = Math.floor(minX/unitsPerTile)*unitsPerTile;
            maxX = Math.ceil(maxX/unitsPerTile)*unitsPerTile;
            minY = Math.floor(minY/unitsPerTile)*unitsPerTile;
            maxY = Math.ceil(maxY/unitsPerTile)*unitsPerTile;

            let done = 0;
            const total = Math.floor((maxX - minX) / unitsPerTile) * Math.floor((maxY - minY) / unitsPerTile);
            const self = this;

            if(total > 100) {
                if(!confirm('De te downloaden kaart is van een groot formaat. Download niet onnodig grote kaarten, dit kan een hoge belasting geven op de bronservers. Wil je doorgaan?')) {
                    reject();
                    return;
                }
            }

            return throttledParallelEachPromise(25, function*() {

                for(let x=minX; x<maxX; x+= unitsPerTile) {
                    for(let y=minY; y<maxY; y+= unitsPerTile) {
                        let imagePromise: Promise<HTMLImageElement>;
                        if (self.mapImageProvider.hasDrawnGrid() && self.getEraseDrawnGrid()) {
                            imagePromise = self.downloadDrawnGridErasedPrintImage(cache, x, y, unitsPerTile, pxPerTile);
                        } else {
                            imagePromise = self.downloadPrintImage(cache, [
                                self.coordinateSystem.make(x, y+unitsPerTile),
                                self.coordinateSystem.make(x+unitsPerTile, y+unitsPerTile),
                                self.coordinateSystem.make(x+unitsPerTile, y),
                                self.coordinateSystem.make(x, y),
                            ], {
                                width: pxPerTile.toString(),
                                height: pxPerTile.toString(),
                            });
                        }

                        yield imagePromise.then((img) => {
                            callback(img, self.coordinateSystem.make(x, y+unitsPerTile), {
                                pxPerUnit,
                                paperMmPerTile,
                            });

                            done++;
                            if(progressCallback) {
                                progressCallback({
                                    done: done,
                                    total: total,
                                })
                            }
                        });
                    }
                }

            }).then(resolve, reject);
        });
    }

    private downloadPrintImage(cache: Cache, coords: C[], params: WmsParams = {}): Promise<HTMLImageElement> {
        const url = this.getWmsUrl(coords, params);

        return cache.fetch(url, () => {
            return new Promise((resolve, reject) => {

                const xhr = new XMLHttpRequest();
                xhr.open('GET', url, true);
                xhr.responseType = 'blob';
                xhr.onload = function (e) {
                    const blob = xhr.response;

                    if (blob === undefined) {
                        reject();
                        return;
                    }

                    const fr = new FileReader();
                    fr.onload = function(e) {
                        // @ts-ignore
                        resolve(fr.result);
                    };
                    fr.readAsDataURL(blob);
                };
                xhr.send(null);
            });
        }).then((result) => {
            return new Promise((resolve, reject) => {
                const img = document.createElement('img');
                img.src = result;
                img.onload = function () {
                    resolve(img);
                };
            });
        });
    }

    private async downloadDrawnGridErasedPrintImage(cache: Cache, tileX: number, tileY: number, unitsPerTile: number, pxPerTile: number): Promise<HTMLImageElement> {
        const pxWindow = 10; // The (even) number of pixels to search for grid line around 'approximate' tileX/tileY pixel
        const black = 0; // Semantic variable for clarity
        const alphaPerChannel = false; // We currently choose to not compute alpha per channel

        const imgToRgba = (img: HTMLImageElement) : ImageData => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, img.width, img.height);
            return ctx.getImageData(0, 0, img.width, img.height);
        };

        const locateGridCenterPixel = (
            approx_px_idx: number,
            baseImageData: ImageData,
            leftImageData: ImageData,
            rightImageData: ImageData,
            ijToImageDataIdx: (number, number) => number,
        ): number => {
            // Find most black line
            let min_px_idx_it = null, min_sum = null;
            for (let px_idx_it = approx_px_idx - pxWindow / 2; px_idx_it < approx_px_idx + pxWindow / 2; px_idx_it++) {
                const im = px_idx_it < 0 ? leftImageData : (px_idx_it >= pxPerTile ? rightImageData : baseImageData);
                const i = px_idx_it < 0 ? px_idx_it + pxPerTile : (px_idx_it >= pxPerTile ? px_idx_it - pxPerTile : px_idx_it);

                let sum = 0;
                for (let px_y_it = 0; px_y_it < pxPerTile; px_y_it++) {
                    const j = px_y_it;
                    const s = ijToImageDataIdx(i, j);
                    sum += im.data[s] + im.data[s + 1] + im.data[s + 2];
                }

                if (min_sum === null || sum < min_sum) {
                    min_sum = sum;
                    min_px_idx_it = px_idx_it;
                }
            }

            return min_px_idx_it;
        };

        const eraseLine = (
            px_idx: number,
            side: number,
            baseImageData: ImageData,
            leftImageData: ImageData,
            rightImageData: ImageData,
            ijToImageDataIdx: (number, number) => number,
        ) => {
            const px_idx_s = px_idx + side;

            const im1 = px_idx < 0 ? leftImageData : (px_idx >= pxPerTile ? rightImageData : baseImageData);
            const i1 = px_idx < 0 ? px_idx + pxPerTile : (px_idx >= pxPerTile ? px_idx - pxPerTile : px_idx);

            const im2 = px_idx_s < 0 ? leftImageData : (px_idx_s >= pxPerTile ? rightImageData : baseImageData);
            const i2 = px_idx_s < 0 ? px_idx_s + pxPerTile : (px_idx_s >= pxPerTile ? px_idx_s - pxPerTile : px_idx_s);

            const alphas_r = [];
            const alphas_g = [];
            const alphas_b = [];

            for (let px_orth_it = 0; px_orth_it < pxPerTile; px_orth_it++) {
                const j = px_orth_it;
                const s1 = ijToImageDataIdx(i1, j);
                const s2 = ijToImageDataIdx(i2, j);

                const alpha_r = (im1.data[s1] - im2.data[s2]) / (black - im2.data[s2]);
                const alpha_g = (im1.data[s1 + 1] - im2.data[s2 + 1]) / (black - im2.data[s2 + 1]);
                const alpha_b = (im1.data[s1 + 2] - im2.data[s2 + 2]) / (black - im2.data[s2 + 2]);

                if (isFinite(alpha_r)) alphas_r.push(alpha_r);
                if (isFinite(alpha_g)) alphas_g.push(alpha_g);
                if (isFinite(alpha_b)) alphas_b.push(alpha_b);
            }

            let alpha_r, alpha_g, alpha_b;
            if (alphaPerChannel) {
                alpha_r = Math.max(0, Math.min(0.999, median(alphas_r)));
                alpha_g = Math.max(0, Math.min(0.999, median(alphas_g)));
                alpha_b = Math.max(0, Math.min(0.999, median(alphas_b)));
            } else {
                alpha_r = alpha_g = alpha_b =  Math.max(0, Math.min(0.999, median([...alphas_r, ...alphas_g, ...alphas_b])));
            }

            for (let px_orth_it = 0; px_orth_it < pxPerTile; px_orth_it++) {
                const j = px_orth_it;
                const s1 = ijToImageDataIdx(i1, j);
                im1.data[s1] = Math.max(0, Math.min(255, Math.round((im1.data[s1] - alpha_r * black) / (1 - alpha_r))));
                im1.data[s1 + 1] = Math.max(0, Math.min(255, Math.round((im1.data[s1 + 1] - alpha_g * black) / (1 - alpha_g))));
                im1.data[s1 + 2] = Math.max(0, Math.min(255, Math.round((im1.data[s1 + 2] - alpha_b * black) / (1 - alpha_b))));
            }
        };

        const wmsUrl = this.getWmsUrl([
            this.coordinateSystem.make(tileX, tileY+unitsPerTile),
            this.coordinateSystem.make(tileX+unitsPerTile, tileY+unitsPerTile),
            this.coordinateSystem.make(tileX+unitsPerTile, tileY),
            this.coordinateSystem.make(tileX, tileY),
        ], {
            width: pxPerTile.toString(),
            height: pxPerTile.toString(),
        });
        const cacheKey = 'drawnGridErased:' + wmsUrl;

        const dataUrl = await cache.fetch(cacheKey, async () => {
            const downloadImagePromise = (x: number, y: number) => this.downloadPrintImage(cache, [
                this.coordinateSystem.make(x, y+unitsPerTile),
                this.coordinateSystem.make(x+unitsPerTile, y+unitsPerTile),
                this.coordinateSystem.make(x+unitsPerTile, y),
                this.coordinateSystem.make(x, y),
            ], {
                width: pxPerTile.toString(),
                height: pxPerTile.toString(),
            });

            const baseImageElement = await downloadImagePromise(tileX, tileY);
            const baseImageData = imgToRgba(baseImageElement);

            const drawnGrid = this.getMipDrawnGrid();
            const ix_min = Math.floor((tileX - drawnGrid.base_x) / drawnGrid.delta_x) - 1;
            const iy_min = Math.floor((tileY - drawnGrid.base_y) / drawnGrid.delta_y) - 1;
            const ix_max = Math.ceil((tileX + unitsPerTile - drawnGrid.base_x) / drawnGrid.delta_x) + 1;
            const iy_max = Math.ceil((tileY + unitsPerTile - drawnGrid.base_y) / drawnGrid.delta_y) + 1;

            for (let ix = ix_min; ix <= ix_max; ix++) {
                const x = drawnGrid.base_x + ix * drawnGrid.delta_x;

                const approx_px_idx = Math.round((x - tileX) / unitsPerTile * pxPerTile);
                if (approx_px_idx < -pxWindow || approx_px_idx > pxPerTile + pxWindow) {
                    continue;
                }

                const leftImageData = approx_px_idx < pxWindow ? imgToRgba(await downloadImagePromise(tileX - unitsPerTile, tileY)) : null;
                const rightImageData = approx_px_idx > pxPerTile - pxWindow ? imgToRgba(await downloadImagePromise(tileX + unitsPerTile, tileY)) : null;

                const ijToImageDataIdx = (i, j) => (j * pxPerTile + i) * 4;

                const min_px_x_it = locateGridCenterPixel(approx_px_idx, baseImageData, leftImageData, rightImageData, ijToImageDataIdx);

                eraseLine(min_px_x_it + 1, +1, baseImageData, leftImageData, rightImageData, ijToImageDataIdx);
                eraseLine(min_px_x_it - 1, -1, baseImageData, leftImageData, rightImageData, ijToImageDataIdx);
                eraseLine(min_px_x_it, +1, baseImageData, leftImageData, rightImageData, ijToImageDataIdx);
            }

            for (let iy = iy_min; iy <= iy_max; iy++) {
                const y = drawnGrid.base_y + iy * drawnGrid.delta_y;

                const approx_px_idx = Math.round((y - tileY) / unitsPerTile * pxPerTile);
                if (approx_px_idx < -pxWindow || approx_px_idx > pxPerTile + pxWindow) {
                    continue;
                }

                const belowImageData = approx_px_idx < pxWindow ? imgToRgba(await downloadImagePromise(tileX, tileY - unitsPerTile)) : null;
                const aboveImageData = approx_px_idx > pxPerTile - pxWindow ? imgToRgba(await downloadImagePromise(tileX, tileY + unitsPerTile)) : null;

                const ijToImageDataIdx = (i, j) => ((pxPerTile - 1 - i) * pxPerTile + j) * 4;

                const min_px_y_it = locateGridCenterPixel(approx_px_idx, baseImageData, belowImageData, aboveImageData, ijToImageDataIdx);

                eraseLine(min_px_y_it + 1, +1, baseImageData, belowImageData, aboveImageData, ijToImageDataIdx);
                eraseLine(min_px_y_it - 1, -1, baseImageData, belowImageData, aboveImageData, ijToImageDataIdx);
                eraseLine(min_px_y_it, +1, baseImageData, belowImageData, aboveImageData, ijToImageDataIdx);
            }

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = baseImageData.width;
            canvas.height = baseImageData.height;
            ctx.putImageData(baseImageData, 0, 0);

            return canvas.toDataURL();
        });

        return new Promise((resolve, reject) => {
            const image = new Image();
            image.src = dataUrl;
            image.onload = () => {
                resolve(image);
            };
        });
    }
}
